import OrderTypeEnum from "@/constants/enums/OrderTypeEnum";
import { Country, CreateOrder, CreateOrderValue, PropertyDto } from "@/types";
import { fileToBase64Raw, getFileTypeFromFile } from "@/utils/FileUtils";
import { DocumentType } from "@/types";
import dayjs from "dayjs";
import { buildPickupCoordinates } from "@/utils/coordinates";

export interface CreateOrderWizard {
  orderType: number;

  price: string;
  currency: string;
  unit: string;
  minMaxPrice?: string;
  priceAdjustPercentage?: string;
  amount: string;
  amountDeviation?: string;
  minimumAmount?: string;
  salesItemId: string;
  properties: PropertyDto[];

  differentAddress: boolean;
  validFrom: Date;
  validTo: Date;
  lastDeliveryDate: Date | null;
  description: string | null;
  address: string;
  postCode: string;
  countryId: string;
  place: string;

  latitude: string;
  longitude: string;
  capturedAt: string | null;

  images: File[];

  boxTypeId: string | null;
  numberOfBoxes: string;
}

export type CreateOrderProductStep = Pick<
  CreateOrderWizard,
  | "price"
  | "currency"
  | "minMaxPrice"
  | "priceAdjustPercentage"
  | "amount"
  | "amountDeviation"
  | "minimumAmount"
  | "properties"
  | "salesItemId"
  | "unit"
>;
export type CreateOrderDeliveryStep = Pick<
  CreateOrderWizard,
  | "differentAddress"
  | "validFrom"
  | "validTo"
  | "lastDeliveryDate"
  | "description"
  | "address"
  | "postCode"
  | "countryId"
  | "place"
  | "latitude"
  | "longitude"
  | "capturedAt"
>;
export type CreateOrderPackagingStep = Pick<CreateOrderWizard, "boxTypeId" | "numberOfBoxes">;

export const CreateOrderInitialValues: CreateOrderWizard = {
  orderType: OrderTypeEnum.SELL,

  price: "",
  currency: "",
  unit: "",
  minMaxPrice: "",
  priceAdjustPercentage: "",
  amount: "",
  amountDeviation: "",
  minimumAmount: "",
  salesItemId: "",
  properties: [],

  differentAddress: false,
  validFrom: dayjs().toDate(),
  validTo: dayjs().add(7, "day").toDate(),
  lastDeliveryDate: null,
  description: "",
  address: "",
  postCode: "",
  countryId: "",
  place: "",

  latitude: "",
  longitude: "",
  capturedAt: null,

  images: [],

  boxTypeId: null,
  numberOfBoxes: "",
};

export const mapCreateOrderToBackendFormat = async (
  data: CreateOrderWizard,
  companyId: number,
  userId: number,
  currencyUnits: CreateOrderValue[],
  countries: Country[]
): Promise<CreateOrder | null> => {
  const convertAllFilesToBase64 = await Promise.all(
    data.images.map(async (file) => {
      const base64File = await fileToBase64Raw(file);
      return {
        image: base64File,
        fileType: getFileTypeFromFile(file),
        documentType: "IMAGE" as DocumentType,
      };
    })
  );

  const currencyUnit = currencyUnits.find((currency) => currency.id === parseFloat(data.currency));
  if (!currencyUnit) {
    return null;
  }

  const orderCountry = countries.find((c) => String(c.id) === data.countryId);

  return {
    companyId: companyId,
    userId: userId,
    orderType: data.orderType!,
    amountDeviation: data.amountDeviation ? parseFloat(data.amountDeviation) : 0,
    amount: parseFloat(data.amount),
    minimumAmount: data.minimumAmount ? parseFloat(data.minimumAmount) : null,
    unit: { id: parseFloat(data.unit), unit: "" },
    price: parseFloat(data.price),
    priceUnit: { id: parseFloat(data.unit), unit: "" },
    priceAdjustPercentage: data.priceAdjustPercentage ? parseFloat(data.priceAdjustPercentage) : null,
    maxPrice: data.orderType === OrderTypeEnum.BUY && data.minMaxPrice ? parseFloat(data.minMaxPrice) : null,
    minPrice: data.orderType === OrderTypeEnum.SELL && data.minMaxPrice ? parseFloat(data.minMaxPrice) : null,
    orderCurrency: currencyUnit,
    validTo: data.validTo,
    validFrom: data.validFrom,
    deliveryStart: null,
    deliveryEnd: data.lastDeliveryDate,
    description: data.description,
    images: convertAllFilesToBase64,
    salesItem: { id: parseFloat(data.salesItemId), value: "" },
    catchDate: null,
    expirationDate: null,
    packingDate: null,
    transportFrom: "",
    promoted: false,
    autoSalesOrder: false,
    orderAddress: data.address
      ? {
        address: data.address,
        postCode: data.postCode,
        place: data.place,
        countryCodeIso3: orderCountry?.iso3 ?? "",
        countryCodeIso2: orderCountry?.iso2 ?? "",
        isEu: orderCountry?.isEu ?? false,
      }
      : null,
    pickupCoordinates: buildPickupCoordinates(data.latitude, data.longitude, data.capturedAt),
    companiesToTradeWith: [],
    packaging: {
      amountInEachBox: 0,
      boxtype: 1,
      isSealed: false,
      numberOfBoxes: 0,
      palletPlaces: 0,
    },
    lastDeliveryDate: data.lastDeliveryDate,
    externalOrderid: null,
    contractId: null,
    batchNumber: null,
    status: null,
    estimatedDelivery: null,
    created: dayjs().toDate(),
    properties: data.properties,
  };
};
